import { useState } from 'preact/hooks';
import { getApiClient } from '@/lib/api-client.js';
import { rateErrorCopy, type ErrorCopy } from '@/lib/error-copy.js';
import { toRateInputsWire } from '@/lib/rate-inputs.js';
import { rateInputRequirements } from '@/state/config.js';
import { rateInputs } from '@/state/rate-inputs.js';
import { applyRateResponse, vehicle } from '@/state/session.js';

interface Rerate {
  /** No-ops while a request is in flight, or when there is no vehicle to re-rate. */
  run: () => Promise<void>;
  pending: boolean;
  error: ErrorCopy | null;
}

/**
 * Re-rates the vehicle already in session. Both callers are recovering from something —
 * the tiers screen after a partial provider outage, the payment screen after the quote
 * went stale — so this is always a repeat of a rate the customer already went through.
 *
 * `onSuccess` runs after the new response is applied, for whatever screen-local state the
 * caller has to settle (a collapsed card, a cleared stale flag).
 */
export function useRerate(onSuccess?: () => void): Rerate {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ErrorCopy | null>(null);

  async function run(): Promise<void> {
    const currentVehicle = vehicle.value;
    if (pending || currentVehicle === null) return;
    setPending(true);
    setError(null);
    try {
      const res = await getApiClient().rate({
        flow_type: 'marketplace',
        vehicle: {
          vin: currentVehicle.vin,
          mileage: currentVehicle.mileage,
        },
        // Replayed, not re-read from the customer form: dropping these would silently
        // re-rate on the dealer's address instead of the buyer's.
        rate_inputs: toRateInputsWire(rateInputRequirements.value, rateInputs.value),
      });
      // Reconcile rather than clear: the vehicle hasn't changed, so whatever the customer
      // already picked should survive being remapped onto the new quote item ids.
      applyRateResponse(res, 'reconcile');
      onSuccess?.();
    } catch (err) {
      setError(rateErrorCopy(err));
    } finally {
      setPending(false);
    }
  }

  return { run, pending, error };
}
